import React, { FC, lazy } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { defer } from 'rxjs';

import { getEnrollmentStatus } from 'api/checkfree/service';
import usePlaceResolver, { variants } from 'components/usePlaceResolver/usePlaceResolver';
import { ApiResponseType } from 'common.types';
import { WORKSPACES } from 'constants.js';

import { CHECKFREE_AUTH_LEVEL, CHECKFREE_MODULES } from './checkfree.constants';
import { ICheckFreeRouteParams, WorkflowModeEnum } from './checkfree.types';

const { ACCOUNT, USER } = CHECKFREE_MODULES;
const { LEVEL_1 } = CHECKFREE_AUTH_LEVEL;
const { DASHBOARD } = WORKSPACES;
const { ADD, MODIFY, NONE } = WorkflowModeEnum;

const BillPayManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'BillPayManagement' */ './billpaymanagement/CheckFreeManageBillPay'
    )
);
const CheckFreeAddAccount = lazy(
  () =>
    import(
      /* webpackChunkName: 'AddBillPayAccount' */ './billpaymanagement/accountManagement/addAccount/AddBillPayAccountWrapper'
    )
);
const CheckFreeUser = lazy(
  () =>
    import(
      /* webpackChunkName: 'CheckfreeUserForm' */ './billpaymanagement/userManagement/UserFormContainer'
    )
);

type EnrollmentResponseType = ApiResponseType<{ enrolled: boolean; role: string }>;

const services = [defer(() => getEnrollmentStatus())];

const CheckFreeRouteHandler: FC = () => {
  const { module = '', workflowMode = NONE } = useParams<ICheckFreeRouteParams>();
  const {
    Placeholder,
    results: [enrollmentResponse]
  } = usePlaceResolver<[EnrollmentResponseType]>({
    services,
    variant: variants.spinner
  });

  const incorrectModule = ![ACCOUNT, USER].includes(module);
  const incorrectWorkflow =
    (module === ACCOUNT && workflowMode !== ADD) ||
    (module === USER && ![ADD, MODIFY].includes(workflowMode));

  const { role } = enrollmentResponse?.data || {};
  const error = enrollmentResponse?.data?.[0]?.userMessage;

  if (Placeholder) {
    return <Placeholder />;
  }

  if (error || role !== LEVEL_1) {
    return <Redirect to={DASHBOARD} />;
  }

  if (incorrectModule || incorrectWorkflow) {
    return <BillPayManagement />;
  }

  return (
    <>
      {module === ACCOUNT && <CheckFreeAddAccount />}
      {module === USER && <CheckFreeUser />}
    </>
  );
};

export default CheckFreeRouteHandler;
